const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export function generateDiagramFilename(
  ext: string,
  clientInitials?: string
): string {
  const date = new Date().toISOString().slice(0, 10); // yyyy-MM-dd
  const initials = clientInitials || "";
  const parts = [date, initials, "NovoNote Diagram"].filter(Boolean);
  return `${parts.join(" ")}.${ext}`;
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadSvg(svgString: string, filename?: string) {
  if (!svgString) return;
  const blob = new Blob([svgString], { type: "image/svg+xml" });
  triggerDownload(blob, filename || generateDiagramFilename("svg"));
}

/**
 * Capture a DOM element to canvas using html2canvas.
 * This handles foreignObject content that browsers refuse to render
 * via the SVG→Image→Canvas path (tainted canvas SecurityError).
 *
 * On Safari at non-100% zoom, foreignObject positioning is buggy.
 * We clone the element into a zoom-corrected off-screen container
 * so html2canvas captures a correctly-rendered version.
 */
async function captureElementToCanvas(
  element: HTMLElement,
  scale = 2
): Promise<HTMLCanvasElement> {
  const html2canvas = (await import("html2canvas")).default;

  if (isSafari) {
    const zoom = window.outerWidth / window.innerWidth;
    if (Math.abs(zoom - 1) > 0.05) {
      const wrapper = document.createElement("div");
      wrapper.style.position = "fixed";
      wrapper.style.left = "-9999px";
      wrapper.style.top = "0";
      wrapper.style.zoom = String(1 / zoom);

      const clone = element.cloneNode(true) as HTMLElement;
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      try {
        return await html2canvas(clone, {
          scale: scale * zoom, // compensate so output resolution stays the same
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });
      } finally {
        document.body.removeChild(wrapper);
      }
    }
  }

  return html2canvas(element, {
    scale,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  });
}

export async function downloadPng(
  element: HTMLElement,
  filename?: string,
  scale = 2
) {
  const canvas = await captureElementToCanvas(element, scale);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (blob) triggerDownload(blob, filename || generateDiagramFilename("png"));
}

/**
 * Rasterize an SVG string to a PNG blob via html2canvas.
 * Mounts the SVG in a hidden off-screen container so html2canvas can render
 * its foreignObject content (which the SVG→Image→Canvas path can't export
 * due to canvas tainting).
 */
export async function pngBlobFromSvgString(
  svgString: string,
  scale = 2
): Promise<Blob> {
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-9999px";
  wrapper.style.top = "0";
  wrapper.style.background = "#ffffff";
  wrapper.innerHTML = svgString;
  document.body.appendChild(wrapper);
  try {
    const canvas = await captureElementToCanvas(wrapper, scale);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    if (!blob) throw new Error("canvas.toBlob returned null");
    return blob;
  } finally {
    document.body.removeChild(wrapper);
  }
}

export async function downloadPdf(element: HTMLElement, filename?: string) {
  const [{ jsPDF }, canvas] = await Promise.all([
    import("jspdf"),
    captureElementToCanvas(element, 2),
  ]);

  const imgData = canvas.toDataURL("image/png");
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  const isLandscape = imgWidth > imgHeight;
  const pdf = new jsPDF({
    orientation: isLandscape ? "landscape" : "portrait",
    unit: "pt",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const availW = pageWidth - margin * 2;
  const availH = pageHeight - margin * 2;
  const scale = Math.min(availW / imgWidth, availH / imgHeight, 1);
  const renderW = imgWidth * scale;
  const renderH = imgHeight * scale;
  const offsetX = margin + (availW - renderW) / 2;
  const offsetY = margin + (availH - renderH) / 2;

  pdf.addImage(imgData, "PNG", offsetX, offsetY, renderW, renderH);
  pdf.save(filename || generateDiagramFilename("pdf"));
}
